import CitySEOHead from "@/components/feature/CitySEOHead";
import ProvoHeroSection from "./components/HeroSection";
import SEOCrossLinks from "@/components/feature/SEOCrossLinks";
import SharedProcessingTimeSection from "@/components/feature/SharedProcessingTimeSection";

const provoProcessingOptions = [
  {
    speed: "24",
    unit: "Hours*",
    label: "SLC Passport Agency",
    tag: "Emergency Only",
    tagColor: "bg-gray-100 text-gray-500",
    features: [
      "Appointment required — no walk-ins accepted",
      "Life-or-death emergencies only",
      "Bereavement or medical crisis",
      "45 miles north — expect traffic on I-15",
      "Expect to wait all day or return next day",
    ],
    highlight: false,
    emergency: true,
  },
  {
    speed: "2–9",
    unit: "Business Days",
    label: "Fast Passport Center",
    tag: "Most Popular",
    tagColor: "bg-[#F6C344] text-[#1A365D]",
    features: [
      "Registered U.S. Department of State Courier",
      "Reserved agency submission",
      "Predictable turnaround — no guessing",
      "Ideal for LDS missions, BYU study abroad, UVU programs",
      "Document review included",
      "Dedicated agent assigned to your case",
      "Daily status updates — never left guessing",
      "Not affected by government shutdowns",
    ],
    highlight: true,
  },
  {
    speed: "3–5",
    unit: "Weeks",
    label: "Fast Passport Center",
    tag: "Standard Expedited",
    tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
    features: [
      "Registered U.S. Department of State Courier",
      "Reserved agency submission",
      "Faster than self-service",
      "Good for planned travel — study abroad semesters, family vacations",
      "Document review included",
      "Dedicated agent assigned to your case",
      "Daily status updates — never left guessing",
      "Not affected by government shutdowns",
    ],
    highlight: false,
    smartSaver: true,
  },
  {
    speed: "1–2",
    unit: "Months",
    label: "Self-Service (Post Office)",
    tag: "Slowest",
    tagColor: "bg-gray-100 text-gray-400",
    features: [
      "No courier access — standard mail only",
      "Provo & Orem post offices book 2–4 weeks out",
      "In-person clerk appointment required",
      "No document review — errors cause rejection",
      "No dedicated agent — you are on your own",
      "Updates only by physical mail letter",
      "No phone or email contact for your case",
      "Can't upgrade speed after submission",
      "Not suitable if travel is within 10 weeks",
    ],
    highlight: false,
    muted: true,
  },
];

export const faqs = [
  {
    q: "Where is the nearest passport agency to Provo?",
    a: "The nearest regional passport agency is the Salt Lake City Passport Agency at 324 S State St, Suite 225, Salt Lake City, UT — about 45 miles north of Provo. Walk-in appointments are rarely available. Our courier service bypasses the agency wait entirely.",
  },
  {
    q: "How fast can I get a passport in Provo, Utah?",
    a: "Through our expedited courier service, passports can be processed as fast as 24 hours for emergencies. Standard expedited service takes 5–7 business days. LDS mission departures are our specialty — we understand the urgency.",
  },
  {
    q: "I'm a BYU student and my study abroad leaves in 10 days — can you help?",
    a: "Absolutely. Ten days is sufficient for our rush or expedited service in most cases. We've processed hundreds of BYU study abroad passports. Place your order immediately and we can guide you on the fastest path.",
  },
  {
    q: "My LDS missionary's mission was reassigned internationally with 72-hour notice — what do I do?",
    a: "Call us now at 877-253-0084. This is exactly the scenario we specialize in. Our 24-hour emergency service is designed for this. We've helped many Provo-area families through last-minute mission reassignments.",
  },
  {
    q: "Do I need to go to a post office for a passport renewal in Provo?",
    a: "For adult renewals (DS-82), no in-person appointment is required. You mail your documents directly to us. First-time applicants and minors must visit an acceptance facility before we take over the courier process.",
  },
  {
    q: "How much does expedited passport service cost in Provo?",
    a: "Our rates start from $299 for standard expedited and from $699 for 24-hour emergency service. This includes our courier fee — significantly less than FedEx and major resellers. The government filing fee ($130–$165) is separate.",
  },
  {
    q: "Can you help with a child's first passport in the Utah County area?",
    a: "Yes. For minors under 16, both parents must appear at an acceptance facility. We guide you through the DS-11 process and handle everything once your in-person step is complete.",
  },
  {
    q: "I live in Orem — can you still help me?",
    a: "Absolutely. We serve the entire Utah Valley, including Orem, Provo, Springville, Spanish Fork, and Payson. Everything is handled by mail — your zip code doesn't matter.",
  },
];

export default function ProvoPage() {
  return (
    <>
      <CitySEOHead
        city="Provo"
        state="Utah"
        stateAbbr="UT"
        route="/provo"
        title="Emergency Passport Provo UT — Expedited Service for BYU, LDS Missions | Fast Passport Center"
        description="Emergency and expedited passport services in Provo, UT — as fast as 24 hours. Serving BYU students, LDS missionaries, Utah Valley families, and UVU students. Registered State Dept. courier. A+ BBB rated. No office visit required."
        geoPlacename="Provo, Utah"
        geoPosition="40.2338;-111.6585"
        faqs={faqs}
        servicesSchema={[
          { name: "Emergency Passport Provo UT", description: "Urgent passport processing for Provo Utah travelers including LDS mission families and BYU students with immediate departure needs." },
          { name: "Expedited Passport Renewal Provo UT", description: "Fast passport renewal for Provo Utah residents with a previously-issued passport." },
          { name: "First-Time Passport Provo UT", description: "New passport application services for Provo Utah and Utah County residents applying for their first passport." },
          { name: "Child Passport Provo UT", description: "Minor passport services for Provo Utah families with children under 16." },
          { name: "Name Change Passport Provo UT", description: "Passport name correction after marriage, divorce, or legal name changes for Provo residents." },
          { name: "Lost Stolen Passport Provo UT", description: "Replacement passport service for Provo residents with lost or stolen travel documents, including DS-64 guidance." },
          { name: "Second Passport Provo UT", description: "Second passport services for Provo Utah frequent international travelers and BYU study-abroad participants." },
        ]}
        keywords="emergency passport Provo, expedited passport Provo Utah, BYU passport, LDS mission passport Provo, passport renewal Utah County"
        statePageName="Utah"
        statePageRoute="/states/utah"
      />
      <div className="font-sans">
        <ProvoHeroSection />

        {/* Sticky Nav */}
        <div className="bg-[#1A365D] text-white sticky top-0 z-40 hidden lg:block">
          <div className="max-w-7xl mx-auto px-8">
            <div className="flex items-center gap-8 h-12 overflow-x-auto">
              {[
                { label: "Processing", href: "#processing" },
                { label: "Services", href: "#services" },
                { label: "Local Area", href: "#local-area" },
                { label: "Travel Timing", href: "#travel-timing" },
                { label: "Testimonials", href: "#testimonials" },
                { label: "Facilities", href: "#facilities" },
                { label: "Compare", href: "#compare" },
                { label: "How It Works", href: "#how-it-works" },
                { label: "FAQ", href: "#faq" },
              ].map((item) => (
                <a key={item.href} href={item.href} className="text-sm text-white/80 hover:text-[#F6C344] whitespace-nowrap transition-colors py-3 border-b-2 border-transparent hover:border-[#F6C344]">
                  {item.label}
                </a>
              ))}
            </div>
          </div>
        </div>

        {/* Processing Time */}
        <SharedProcessingTimeSection
          sectionId="processing"
          cityName="Provo"
          headingExtra="UT"
          introText="The SLC Passport Agency is 45 miles north on I-15 and appointments book out weeks in advance — walk-ins are only for verified life-or-death emergencies. Our registered courier service hand-delivers your application daily, so LDS mission families, BYU students, Utah Valley University travelers, and Utah County families get their passports on a predictable timeline — no appointment lottery, no waiting room, no drive to Salt Lake."
          options={provoProcessingOptions}
          emergencyDescription="The SLC Passport Agency at 324 S State St, 45 miles north of Provo, serves verified life-or-death emergencies only. You must call NPIC (877-487-2778) and have proof of imminent international travel within 72 hours. Appointments are extremely limited and not guaranteed — and you're competing with the entire Wasatch Front for a slot."
          disclaimerText="* Emergency processing availability depends on U.S. Department of State volume, appointment slots, and your specific circumstances. Travel must be within 72 hours for agency emergency appointments. Courier processing times are based on business days and are not guaranteed — but our daily submission schedule gives Provo and Utah Valley residents the fastest legally available route."
          afterCardsContent={
            <div className="mt-16">
              <div className="text-center mb-10">
                <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Popular Destinations</p>
                <h3 className="font-serif text-2xl md:text-3xl font-bold text-[#1A365D] mb-4">Where Provo Travelers Are Going</h3>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-8">
                {[
                  { city: "Jerusalem, Israel", flag: "🇮🇱", note: "BYU Jerusalem Center" },
                  { city: "Sao Paulo, Brazil", flag: "🇧🇷", note: "LDS mission hub" },
                  { city: "Manila, Philippines", flag: "🇵🇭", note: "LDS mission" },
                  { city: "London, UK", flag: "🇬🇧", note: "BYU & tech travel" },
                  { city: "Mexico City, Mexico", flag: "🇲🇽", note: "Mission & heritage" },
                  { city: "Seoul, South Korea", flag: "🇰🇷", note: "Tech & mission" },
                ].map((dest) => (
                  <div key={dest.city} className="flex items-center gap-3 bg-[#F7F8FA] rounded-lg p-4">
                    <span className="text-2xl">{dest.flag}</span>
                    <div>
                      <p className="font-bold text-[#1A365D] text-sm">{dest.city}</p>
                      <p className="text-gray-500 text-xs">{dest.note}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { stat: "33,000+", label: "BYU students served" },
                  { stat: "24 hrs", label: "Fastest turnaround" },
                  { stat: "190+", label: "Countries we've covered" },
                  { stat: "4.9★", label: "Customer rating" },
                ].map((s) => (
                  <div key={s.label} className="text-center bg-[#EEF2F8] rounded-lg p-5">
                    <p className="font-bold text-[#1A365D] text-2xl mb-1">{s.stat}</p>
                    <p className="text-gray-600 text-sm">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>
          }
        />

        {/* Services */}
        <section id="services" className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">What We Handle</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Passport Services for Provo & Utah County</h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { icon: "ri-passport-line", title: "Passport Renewal", desc: "Adult renewals by mail with no post office visit. Fastest route to a new 10-year passport for previously issued adult passports." },
                { icon: "ri-user-add-line", title: "First-Time Passport", desc: "Complete DS-11 guidance and acceptance facility support for first-time applicants throughout Utah County." },
                { icon: "ri-parent-line", title: "Child Passport", desc: "Minor under 16? Both parents must consent. We guide you through the DS-11 minor requirements and acceptance clerk appointment." },
                { icon: "ri-alert-line", title: "Lost or Stolen Passport", desc: "DS-64 filing plus replacement application processing. We handle the paperwork and the courier leg together." },
                { icon: "ri-file-edit-line", title: "Name Change Passport", desc: "Post-marriage or legal name change? We process DS-5504 corrections for recently issued passports and full renewals for older ones." },
                { icon: "ri-global-line", title: "Second Passport", desc: "Required for travelers visiting multiple countries with conflicting visa stamp policies. We handle this specialized request regularly." },
              ].map((svc) => (
                <div key={svc.title} className="bg-white rounded-lg p-7 border border-gray-100">
                  <div className="w-12 h-12 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4">
                    <i className={`${svc.icon} text-[#1A365D] text-2xl`}></i>
                  </div>
                  <h3 className="font-bold text-[#1A365D] text-lg mb-2">{svc.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{svc.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Local Area */}
        <section id="local-area" className="bg-white py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
              <div>
                <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Provo Context</p>
                <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-6">Why Provo Residents Need Expedited Passport Help</h2>
                <div className="text-gray-600 text-sm leading-relaxed space-y-4">
                  <p>Provo occupies a singular position in the American passport landscape. With Brigham Young University enrolling over 33,000 students — many of whom participate in BYU's extensive international study and service programs — and with the LDS Church's missionary program dispatching thousands of young adults each year to countries across six continents, the passport urgency generated by Utah County is extraordinary for a metro area of its size. Mission calls regularly arrive with departure dates just 2–4 weeks away. When visa complications or mission boundary changes force reassignments to international destinations, the timeline can compress to 48–72 hours.</p>
                  <p>Beyond the LDS community, Provo has emerged as a technology hub in its own right. Qualtrics, Ancestry, and dozens of other software companies have offices along the University Parkway tech corridor, sending product managers and engineers to international conferences in London, Berlin, and Singapore with limited advance notice. Utah Valley University's 40,000+ students increasingly participate in global exchange programs, creating parallel demand for passport services.</p>
                  <p>The challenge for Provo residents is geographic: the Salt Lake City regional passport agency is 45 miles north, and securing a walk-in appointment there requires proof of imminent departure. Post offices in Utah County book passport appointment slots 2–4 weeks out during peak seasons. Our courier service solves both problems — we hand-deliver your application to the State Department in Washington, D.C. and FedEx your completed passport directly to your Provo doorstep.</p>
                </div>
              </div>
              <div className="space-y-4">
                <img
                  src="https://readdy.ai/api/search-image?query=Aerial%20view%20of%20Brigham%20Young%20University%20campus%20in%20Provo%20Utah%20with%20iconic%20Y%20Mountain%20behind%20it%2C%20red%20brick%20academic%20buildings%20surrounded%20by%20well-manicured%20grounds%2C%20Utah%20Valley%20and%20Utah%20Lake%20visible%20in%20the%20distance%2C%20clear%20sunny%20day%20with%20blue%20sky%2C%20professional%20university%20campus%20photography%2C%20no%20readable%20text%20or%20signs&width=700&height=450&seq=provo-byu-campus&orientation=landscape"
                  alt="BYU campus in Provo Utah with Y Mountain backdrop"
                  className="w-full h-[250px] object-cover rounded-lg"
                />
                <div className="grid grid-cols-2 gap-4">
                  {[
                    { label: "Nearest Passport Agency", value: "SLC — 45 miles north" },
                    { label: "BYU Enrollment", value: "33,000+ students" },
                    { label: "Airport", value: "Provo Airport (PVU) / SLC" },
                    { label: "Key Industries", value: "Tech, Education, LDS" },
                  ].map((stat) => (
                    <div key={stat.label} className="bg-[#F7F8FA] rounded-lg p-4">
                      <p className="text-xs text-gray-500 mb-1">{stat.label}</p>
                      <p className="font-bold text-[#1A365D] text-sm">{stat.value}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Travel Timing */}
        <section id="travel-timing" className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">When to Apply</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Provo Passport Timing Guide</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {[
                { scenario: "LDS Mission Reassignment", window: "Apply within 24 hours of notice", urgency: "High", icon: "ri-global-line", detail: "International reassignments can happen with 48–72 hours of notice. Our emergency service is your only option." },
                { scenario: "BYU Study Abroad", window: "Apply 3 weeks before departure", urgency: "Medium-High", icon: "ri-graduation-cap-line", detail: "BYU Jerusalem, London, or Southeast Asia programs fill up. Apply immediately once accepted." },
                { scenario: "UVU International Program", window: "Apply 4 weeks before departure", urgency: "Medium", icon: "ri-book-line", detail: "UVU's growing global programs need passport support. Our rush service handles 2–4 week windows comfortably." },
                { scenario: "Tech Company International Travel", window: "Apply as soon as trip is confirmed", urgency: "Variable", icon: "ri-briefcase-line", detail: "Qualtrics, Ancestry, and Silicon Slopes companies book last-minute. We've got you covered for renewal or first-time." },
              ].map((item) => (
                <div key={item.scenario} className="bg-white rounded-lg p-6 border border-gray-100">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8] mb-4">
                    <i className={`${item.icon} text-[#1A365D] text-lg`}></i>
                  </div>
                  <h3 className="font-bold text-[#1A365D] text-base mb-1">{item.scenario}</h3>
                  <p className="text-[#F6C344] text-xs font-bold mb-2 uppercase">{item.window}</p>
                  <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded-full mb-3 ${item.urgency === "High" ? "bg-red-100 text-red-700" : item.urgency.includes("Medium") ? "bg-amber-100 text-amber-700" : "bg-green-100 text-green-700"}`}>{item.urgency} Urgency</span>
                  <p className="text-gray-600 text-sm leading-relaxed">{item.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Testimonials */}
        <section id="testimonials" className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Real Stories</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Provo & Utah Valley Clients</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {[
                { name: "Sister T. Allred", location: "Provo, UT", text: "Got my mission call to Argentina on a Friday and needed my passport by Tuesday. Fast Passport Center made it happen. I was on the plane as planned. Incredible service.", stars: 5 },
                { name: "Jake L.", location: "Orem, UT", text: "BYU study abroad in London, 11 days before departure my passport was expired. They renewed it in 5 days. Literally saved my semester abroad.", stars: 5 },
                { name: "Sarah C.", location: "Springville, UT", text: "Family of 4 needed passports for a Europe trip. They handled all 4 applications at once and we had everything in 7 days. Process was way easier than I expected.", stars: 5 },
              ].map((t) => (
                <div key={t.name} className="bg-white rounded-lg p-7 border border-gray-100">
                  <div className="flex gap-0.5 mb-4">
                    {Array.from({ length: t.stars }).map((_, i) => <i key={i} className="ri-star-fill text-[#F6C344] text-sm"></i>)}
                  </div>
                  <p className="text-gray-700 text-sm leading-relaxed mb-5 italic">"{t.text}"</p>
                  <div>
                    <p className="font-bold text-[#1A365D] text-sm">{t.name}</p>
                    <p className="text-gray-500 text-xs">{t.location}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Acceptance Facilities */}
        <section id="facilities" className="bg-white py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Local Offices</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Acceptance Facilities Near Provo & Utah County</h2>
              <p className="text-gray-500 max-w-2xl mx-auto text-sm">First-time applicants and minors must visit an acceptance facility before our courier process begins.</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { name: "Provo Post Office", address: "95 W 100 S, Provo", hours: "Mon–Fri 9am–5pm, Sat 9am–12pm", type: "Acceptance Facility" },
                { name: "Orem Post Office", address: "760 S State St, Orem", hours: "Mon–Fri 9am–5pm", type: "Acceptance Facility" },
                { name: "Springville Post Office", address: "45 S Main St, Springville", hours: "Mon–Fri 9am–5pm", type: "Acceptance Facility" },
                { name: "Spanish Fork Post Office", address: "40 N Main St, Spanish Fork", hours: "Mon–Fri 9am–5pm", type: "Acceptance Facility" },
                { name: "Payson Post Office", address: "20 E Utah Ave, Payson", hours: "Mon–Fri 9am–5pm", type: "Acceptance Facility" },
                { name: "SLC Passport Agency", address: "324 S State St, Suite 225, SLC", hours: "Mon–Fri 8am–3pm (by appt)", type: "Regional Agency (45 mi)" },
              ].map((fac) => (
                <div key={fac.name} className="border border-gray-200 rounded-lg p-6">
                  <span className="inline-block bg-[#EEF2F8] text-[#1A365D] text-xs font-semibold px-2 py-0.5 rounded-full mb-3">{fac.type}</span>
                  <h3 className="font-bold text-[#1A365D] text-base mb-1">{fac.name}</h3>
                  <p className="text-gray-600 text-sm mb-1"><i className="ri-map-pin-line mr-1.5"></i>{fac.address}</p>
                  <p className="text-gray-500 text-xs"><i className="ri-time-line mr-1.5"></i>{fac.hours}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Comparison */}
        <section id="compare" className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-5xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Why Choose Us</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Fast Passport Center vs. Alternatives</h2>
            </div>

            <div className="bg-amber-50 border border-[#F6C344]/40 rounded-lg p-5 md:p-6 mb-8">
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <div className="flex-1">
                  <h3 className="font-bold text-base text-[#1A365D] mb-1">Passport Agency Appointment Not Available?</h3>
                  <p className="text-sm text-gray-600">Don't let a booked-out agency appointment block your travel plans. We bypass the agency bottleneck with reserved daily courier slots — faster and no appointment roulette.</p>
                </div>
                <a
                  href="https://fastpassportcenter.com/checkout/"
                  className="inline-flex items-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-sm px-5 py-2.5 rounded-md transition-all whitespace-nowrap"
                >
                  Get Started <i className="ri-arrow-right-line"></i>
                </a>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-[#1A365D] text-white">
                    <th className="text-left px-4 py-3 font-semibold rounded-tl-lg">Feature</th>
                    <th className="text-center px-4 py-3 font-semibold text-[#F6C344]">Fast Passport Center</th>
                    <th className="text-center px-4 py-3 font-semibold">SLC Agency (45 mi)</th>
                    <th className="text-center px-4 py-3 font-semibold rounded-tr-lg">FedEx / UPS</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ["No office visit required", "✓", "✗", "✗"],
                    ["As fast as 24 hours", "✓", "Same-day only", "✗"],
                    ["Price vs. resellers", "30–100% less", "Govt fee only", "Full markup"],
                    ["Registered State Dept. courier", "✓", "N/A", "✗"],
                    ["LDS mission specialist", "✓", "Limited", "✗"],
                    ["Document error review", "✓", "✗", "✗"],
                    ["Works for minors under 16", "✓", "Limited", "✗"],
                    ["FedEx to your Provo doorstep", "✓", "Pick up in person", "Partial"],
                    ["A+ BBB rated", "✓", "N/A", "Varies"],
                  ].map(([feat, us, agency, fedex], i) => (
                    <tr key={feat} className={i % 2 === 0 ? "bg-white" : "bg-[#F7F8FA]"}>
                      <td className="px-4 py-3 text-gray-700 font-medium">{feat}</td>
                      <td className="px-4 py-3 text-center text-green-600 font-semibold">{us}</td>
                      <td className="px-4 py-3 text-center text-gray-500">{agency}</td>
                      <td className="px-4 py-3 text-center text-gray-500">{fedex}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>

        {/* Mistakes */}
        <section className="bg-white py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Avoid Delays</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Common Provo Passport Mistakes</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                { title: "Waiting until after mission call", desc: "Many families wait until the call letter arrives to start. Apply the day you get your call — not the week before departure." },
                { title: "Underestimating post office wait times", desc: "Provo and Orem post offices book acceptance appointments 2–4 weeks out in spring. Don't assume you can walk in." },
                { title: "Missing the photo requirements", desc: "White background, no glasses, specific dimensions. We review all photos before submission to prevent rejection." },
                { title: "Forgetting the 6-month validity rule", desc: "Brazil, Philippines, and many mission countries require 6 months of validity beyond your departure date." },
                { title: "Not applying for minors early enough", desc: "Children under 16 require both parents. Start the minor application process at least 4 weeks before departure." },
                { title: "Sending documents to wrong address", desc: "Many families accidentally mail to USPS processing centers instead of our intake address. We send a clear FedEx prepaid label." },
              ].map((m) => (
                <div key={m.title} className="flex gap-4">
                  <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-100 flex-shrink-0 mt-0.5">
                    <i className="ri-close-circle-line text-red-600 text-base"></i>
                  </div>
                  <div>
                    <h3 className="font-bold text-[#1A365D] text-sm mb-1">{m.title}</h3>
                    <p className="text-gray-600 text-sm leading-relaxed">{m.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How It Works */}
        <section id="how-it-works" className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">The Process</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">How It Works for Provo Residents</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              {[
                { step: "1", title: "Order Online", desc: "Select your service tier and complete our secure order form. Takes 5 minutes." },
                { step: "2", title: "Mail Your Documents", desc: "Use our prepaid FedEx label to send your application and passport from any drop box in Utah County." },
                { step: "3", title: "We Courier to D.C.", desc: "Our registered State Dept. courier hand-delivers your application directly to the passport processing facility in Washington, D.C." },
                { step: "4", title: "Delivered to Your Door", desc: "Your completed passport ships via FedEx Priority Overnight to your Provo, Orem, or Springville address." },
              ].map((s) => (
                <div key={s.step} className="text-center">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#1A365D] text-[#F6C344] font-bold text-xl mx-auto mb-4">{s.step}</div>
                  <h3 className="font-bold text-[#1A365D] text-base mb-2">{s.title}</h3>
                  <p className="text-gray-600 text-sm leading-relaxed">{s.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ */}
        <section id="faq" className="bg-white py-16 md:py-20">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Questions</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Provo Passport FAQs</h2>
            </div>
            <div className="space-y-4">
              {faqs.map((faq, i) => (
                <details key={i} className="border border-gray-200 rounded-lg group">
                  <summary className="flex items-center justify-between px-6 py-4 cursor-pointer list-none">
                    <h4 className="font-semibold text-[#1A365D] text-sm pr-4">{faq.q}</h4>
                    <i className="ri-add-line text-[#1A365D] flex-shrink-0 group-open:hidden"></i>
                    <i className="ri-subtract-line text-[#1A365D] flex-shrink-0 hidden group-open:block"></i>
                  </summary>
                  <div className="px-6 pb-4 text-gray-600 text-sm leading-relaxed">{faq.a}</div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Helpful Resources */}
        <section className="bg-[#F7F8FA] py-16 md:py-20">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <div className="text-center mb-12">
              <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-3">Official Links</p>
              <h2 className="font-serif text-3xl md:text-4xl font-bold text-[#1A365D] mb-4">Helpful Resources for Provo Passport Applicants</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                { title: "U.S. State Dept. Passport Info", url: "https://travel.state.gov", icon: "ri-government-line" },
                { title: "SLC Passport Agency (45 mi from Provo)", url: "https://travel.state.gov/content/travel/en/passports/get-fast/passport-agencies.html", icon: "ri-building-line" },
                { title: "BYU Jerusalem Center Info", url: "https://kennedy.byu.edu/jerusalem-center/", icon: "ri-graduation-cap-line" },
                { title: "DS-82 Renewal Form", url: "https://travel.state.gov/content/travel/en/passports/need-passport/ds-82.html", icon: "ri-file-text-line" },
                { title: "Provo Airport (PVU)", url: "https://www.provoairport.com", icon: "ri-flight-takeoff-line" },
                { title: "Utah DMV (ID Documentation)", url: "https://dld.utah.gov", icon: "ri-id-card-line" },
              ].map((r) => (
                <a key={r.title} href={r.url} target="_blank" rel="noopener noreferrer nofollow" className="flex items-center gap-4 bg-white rounded-lg p-5 border border-gray-200 hover:border-[#F6C344] transition-colors group">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-[#EEF2F8] flex-shrink-0">
                    <i className={`${r.icon} text-[#1A365D] text-lg`}></i>
                  </div>
                  <span className="text-[#1A365D] font-medium text-sm group-hover:text-[#F6C344] transition-colors">{r.title}</span>
                  <i className="ri-external-link-line text-gray-400 ml-auto"></i>
                </a>
              ))}
            </div>
          </div>
        </section>
      </div>

      <SEOCrossLinks currentRoute="/cities/provo" state="UT" />

      {/* Final CTA */}
      <section className="bg-[#1A365D] py-16 md:py-20">
        <div className="max-w-4xl mx-auto px-4 md:px-8 text-center">
          <p className="text-[#F6C344] text-xs font-bold uppercase tracking-[0.2em] mb-4">Get Started Today</p>
          <h2 className="font-serif text-3xl md:text-4xl font-bold text-white mb-6">Provo's Fastest Passport Service — BYU & Mission Specialists</h2>
          <p className="text-white/70 text-lg mb-8 max-w-2xl mx-auto">Mission call. Study abroad. Family vacation. We've seen it all from Provo. As fast as 24 hours, delivered to your door.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="https://fastpassportcenter.com/checkout/" className="inline-flex items-center justify-center gap-2 bg-[#F6C344] hover:bg-[#e5b430] text-[#1A365D] font-bold text-base px-8 py-4 rounded-md transition-all whitespace-nowrap">
              Start My Passport <i className="ri-arrow-right-line"></i>
            </a>
            <a href="tel:8772530084" className="inline-flex items-center justify-center gap-2 border-2 border-white/30 hover:border-white text-white font-semibold text-base px-8 py-4 rounded-md transition-all whitespace-nowrap">
              <i className="ri-phone-line"></i>877-253-0084
            </a>
          </div>
        </div>
      </section>
    </>
  );
}